<template>
    <div class="dashboard-layout min-h-screen antialiased">
        <YpHeader id="header">
            <template #top-header>
                <YpHeaderAlert v-if="alertMessage">
                    <div class="text-left small_desktop:text-center text-pretty">
                        Você deve confirmar seu e-mail para começar a utilizar as funções do painel.
                        <a
                            href="#"
                            class="text-left font-semibold"
                            @click.prevent="handleOpenOtp"
                        >
                            Clique aqui para confirmar seu e-mail.
                        </a>
                    </div>
                </YpHeaderAlert>
            </template>
        </YpHeader>

        <YpSidebar />

        <YpMain id="main-content">
            <RouterView
                :key="$route.fullPath"
                v-slot="{ Component }"
            >
                <Suspense>
                    <Transition
                        name="fade"
                        mode="out-in"
                    >
                        <div v-show="Component">
                            <Component :is="Component" />
                        </div>
                    </Transition>

                    <template #fallback>
                        <Loading show />
                    </template>
                </Suspense>
            </RouterView>
        </YpMain>

        <TabNavigation />

        <ModalOtpConfirmation
            v-if="showModalOtp"
            :visible="showModalOtp"
            @closed="handleClose"
            @error="handleErrorOtp"
            @success="handleSuccessOtp"
        />

        <ModalPrivacyPolicyUpdate
            v-if="showModalPolicyUpdate"
            :visible="showModalPolicyUpdate"
            @closed="handleClosePolicyUpdate"
            @error="handleErrorTerms"
        />
    </div>
</template>

<script>
import { defineAsyncComponent } from 'vue';
import { mapGetters } from 'vuex';
import { POSITION } from 'vue-toastification';

// Components
import YpHeader from '@/components/layout/YpHeader';
import YpMain from '@/components/layout/YpMain';
import YpSidebar from '@/components/layout/sidebar/YpSidebar';
import TabNavigation from '@/components/layout/TabNavigation';
import YpHeaderAlert from '@/components/core/YpHeaderAlert.vue';
import Loading from '@/components/Loading';

// Mixins
import intercom from '@/mixins/intercom';

export default {
    name: 'Dashboard',

    components: {
        YpHeader,
        YpMain,
        YpSidebar,
        TabNavigation,
        YpHeaderAlert,
        Loading,
        ModalOtpConfirmation: defineAsyncComponent(() => import('@/components/register/ModalOtpConfirmation.vue')),
        ModalPrivacyPolicyUpdate: defineAsyncComponent(() => import('@/components/register/ModalPrivacyPolicyUpdate.vue')),
    },

    mixins: [intercom],

    data() {
        return {
            showModalOtp: false,
            showModalPolicyUpdate: false,
        };
    },

    async mounted() {
        await this.$store.dispatch('auth/getIntercomSignature');

        this.createObserverInstance();

        this.bootIntercom();

        this.$store.dispatch('global_loading/changeLoadingStatus', false);
    },

    created() {
        // TODO: verify if user registred before the updated policy (recomended a getter in the vuex)
        this.showModalPolicyUpdate = this.acceptedTerms;
    },

    computed: {
        ...mapGetters({
            userRoleType: 'profile/userRoleType',
            userRef: 'profile/userRef',
            authUser: 'auth/user',
            hasEmailConfirmation: 'auth/hasEmailConfirmation',
            acceptedTerms: 'auth/acceptedTerms',
        }),

        navbarCollapse() {
            return this.$store.getters['auth/user'] ? this.$store.getters['navbar/navbarCollapse'] : false;
        },

        isAlertVisible() {
            return true;
        },

        alertMessage() {
            return !this.hasEmailConfirmation ? 'Você deve confirmar seu e-mail para começar a utilizar as funções do painel. Clique aqui para confirmar seu e-mail.' : '';
        },
    },

    methods: {
        bootIntercom() {
            const { authUser, $auth_intercom_signature, userRoleType, userRef } = this;
            if (!window.Intercom || typeof window.Intercom !== 'function' || !authUser) {
                return;
            }

            window.Intercom('boot', {
                app_id: import.meta.env.VITE_INTERCOM_ID,
                user_id: `partner_${authUser.data.id}`,
                user_hash: $auth_intercom_signature,
                name: authUser.data.name,
                ref: userRef,
                email: authUser.data.email,
                user_type: userRoleType,
                social_name: authUser.data.social_name,
                chat_enabled: this.intercomChatIsEnabled,
            });
        },

        handleOpenOtp() {
            this.showModalOtp = true;
        },

        handleClose() {
            this.showModalOtp = false;
        },

        handleClosePolicyUpdate() {
            this.showModalPolicyUpdate = false;
        },

        handleErrorOtp() {
            this.$toast.error('Ocorreu um erro. Entre em contato com o nosso suporte: suporte@yampi.com.br.', {
                position: POSITION.BOTTOM_CENTER,
                icon: false,
            });
        },

        handleErrorTerms(msg) {
            this.$toast.error(msg, {
                position: POSITION.BOTTOM_CENTER,
                icon: false,
            });
        },

        handleSuccessOtp() {
            this.showModalOtp = false;
            this.$toast.success('E-mail confirmado com sucesso! Agora você pode utilizar as funções do painel de parceiros.', {
                position: POSITION.BOTTOM_LEFT,
                icon: false,
            });
        },
    },
};
</script>

<style lang="scss">
.st-pusher {
    width: 5000px;
    height: 100%;
    display: none;
    position: absolute;
    top: 0;
    background-color: theme('colors.transparent.light.22');
    visibility: hidden;
    opacity: 1;
    transition: all 0.3s;
}

.dashboard-layout.ak-open {
    #header {
        z-index: 998;
    }

    #sidebar {
        z-index: 997;
    }
}

.dashboard-layout .dashboard-layout__content {
    transition: all 0.4s;
}

@media only screen and (min-width: 959px) {
    .dashboard-layout.is-collapse .dashboard-layout__content {
        padding-left: 72px;
    }
}

@media only screen and (max-width: 959px) {
    html.slide-nav-active {
        body {
            position: fixed;
            overflow: hidden !important;
            width: 100%;
            height: 100%;
        }

        .dashboard-layout {
            overflow: hidden !important;

            // .sidebar {
            //     left: 0;
            // }

            .st-pusher-nav {
                display: block;
                visibility: visible;
                opacity: 1;
            }
        }
    }
}
</style>
